import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { transactionService } from '../services/transaction.service';
import { moldService } from '../services/mold.service';
import { productionLogService } from '../services/production-log.service';
import { notificationService } from '../services/notification.service';
import { moldHistoryService } from '../services/mold-history.service';
import { useAuth } from '../context/AuthContext';
import { Mold } from '../types';
import { HistoryType } from '../types/history.types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: isMobile ? 2 : 3 }}>{children}</Box>}
    </div>
  );
};

// 출고 처리 컴포넌트
const CheckOutForm: React.FC<{ initialMoldId?: string }> = ({ initialMoldId }) => {
  const { user } = useAuth();
  const [selectedMold, setSelectedMold] = useState<Mold | null>(null);
  const [molds, setMolds] = useState<Mold[]>([]);
  const [workType, setWorkType] = useState<HistoryType>('production');
  const [purpose, setPurpose] = useState('');
  const [expectedShots, setExpectedShots] = useState<number | ''>('');
  const [expectedReturn, setExpectedReturn] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  useEffect(() => {
    loadAvailableMolds();
  }, []);

  useEffect(() => {
    // initialMoldId가 있고 molds가 로드되면 자동 선택
    if (initialMoldId && molds.length > 0) {
      const mold = molds.find(m => m.id === initialMoldId);
      if (mold) {
        setSelectedMold(mold);
      }
    }
  }, [initialMoldId, molds]);

  const loadAvailableMolds = async () => {
    try {
      const allMolds = await moldService.getMolds();
      const availableMolds = allMolds.filter(mold => mold.status === 'stored');
      setMolds(availableMolds);
    } catch (error) {
      console.error('금형 목록 로드 실패:', error);
    }
  };
  const handleCheckOut = async () => {
    if (!selectedMold || !expectedReturn || !user) {
      setMessage({type: 'error', text: '필수 필드를 입력해주세요.'});
      return;
    }

    setLoading(true);
    try {
      const transaction = await transactionService.checkOut(
        selectedMold.id || '',
        user.uid,
        purpose || workType,
        typeof expectedShots === 'number' ? expectedShots : 0,
        expectedReturn
      );

      // 출고 알림 발송 (백그라운드)
      notificationService.sendCheckOutAlert(user.uid, selectedMold, transaction).catch(err => {
        console.error('출고 알림 발송 실패:', err);
      });
      
      setMessage({type: 'success', text: '출고 처리가 완료되었습니다.'});
      setSelectedMold(null);
      setWorkType('production');
      setPurpose('');
      setExpectedShots('');
      setExpectedReturn(null);
      loadAvailableMolds(); // 목록 새로고침
    } catch (error) {
      setMessage({type: 'error', text: '출고 처리에 실패했습니다.'});
    }
    setLoading(false);
  };

  return (
    <Card sx={{ height: 'fit-content' }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          금형 출고
        </Typography>
        
        {message && (
          <Alert severity={message.type} sx={{ mb: { xs: 2, md: 2 } }}>
            {message.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, md: 3 } }}>
          <Box>
            <Autocomplete
              options={molds}
              getOptionLabel={(option) => `${option.moldNumber} - ${option.moldName}`}
              value={selectedMold}
              onChange={(_, newValue) => setSelectedMold(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="금형 선택"
                  required
                  fullWidth
                  size="medium"
                />
              )}
            />
          </Box>

          <Box>
            <FormControl fullWidth required size="medium">
              <InputLabel>작업 유형</InputLabel>
              <Select
                value={workType}
                label="작업 유형"
                onChange={(e) => setWorkType(e.target.value as HistoryType)}
              >
                <MenuItem value="production">생산</MenuItem>
                <MenuItem value="trial_production">시사출</MenuItem>
                <MenuItem value="maintenance">정비</MenuItem>
                <MenuItem value="repair">수리</MenuItem>
                <MenuItem value="inspection">점검</MenuItem>
                <MenuItem value="modification">개조</MenuItem>
                <MenuItem value="accident">사고</MenuItem>
                <MenuItem value="other">기타</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <TextField
              label="상세 설명 (선택)"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              fullWidth
              size="medium"
              multiline
              rows={2}
            />
          </Box>

          <Box>
            <TextField
              label="예상 쇼트 수"
              type="number"
              value={expectedShots}
              onChange={(e) => {
                const val = e.target.value;
                setExpectedShots(val === '' ? '' : Number(val));
              }}
              fullWidth
              size="medium"
            />
          </Box>

          <Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="예상 반납일"
                value={expectedReturn}
                onChange={(newValue) => setExpectedReturn(newValue)}
                format="yyyy/MM/dd"
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    required: true,
                    size: 'medium'
                  } 
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box>
            <Button
              variant="contained"
              onClick={handleCheckOut}
              disabled={loading}
              fullWidth
              sx={{ 
                height: { xs: 48, md: 40 },
                fontSize: { xs: '1rem', md: '0.875rem' },
                fontWeight: 'bold'
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : '출고 처리'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// 입고 처리 컴포넌트
const CheckInForm: React.FC<{ initialMoldId?: string }> = ({ initialMoldId }) => {
  const { user } = useAuth();
  const [selectedMold, setSelectedMold] = useState<Mold | null>(null);
  const [inUseMolds, setInUseMolds] = useState<Mold[]>([]);
  const [actualShots, setActualShots] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [loadingProduction, setLoadingProduction] = useState(false);
  const [productionTotal, setProductionTotal] = useState<number>(0);
  const [productionLogCount, setProductionLogCount] = useState<number>(0);

  useEffect(() => {
    loadInUseMolds();
  }, []);

  useEffect(() => {
    // initialMoldId가 있고 inUseMolds가 로드되면 자동 선택
    if (initialMoldId && inUseMolds.length > 0) {
      const mold = inUseMolds.find(m => m.id === initialMoldId);
      if (mold) {
        setSelectedMold(mold);
      }
    }
  }, [initialMoldId, inUseMolds]);

  // 금형 선택 시 생산이력 조회
  useEffect(() => {
    const loadProductionLogs = async () => {
      if (!selectedMold?.id) {
        setProductionTotal(0);
        setProductionLogCount(0);
        setActualShots('');
        return;
      }

      try {
        setLoadingProduction(true);
        const logs = await productionLogService.getByMoldId(selectedMold.id);
        const total = productionLogService.calculateTotal(logs);
        
        setProductionTotal(total);
        setProductionLogCount(logs.length);
        setActualShots(total); // 자동 입력
      } catch (error) {
        console.error('생산이력 조회 실패:', error);
        setProductionTotal(0);
        setProductionLogCount(0);
      } finally {
        setLoadingProduction(false);
      }
    };

    loadProductionLogs();
  }, [selectedMold]);

  const loadInUseMolds = async () => {
    try {
      const allMolds = await moldService.getMolds();
      const inUseMolds = allMolds.filter(mold => mold.status === 'in-use');
      setInUseMolds(inUseMolds);
    } catch (error) {
      console.error('사용중 금형 목록 로드 실패:', error);
    }
  };

  const handleCheckIn = async () => {
    if (!selectedMold || !user) {
      setMessage({type: 'error', text: '금형을 선택해주세요.'});
      return;
    }

    setLoading(true);
    try {
      const transaction = await transactionService.checkIn(
        selectedMold.id || '',
        user.uid,
        typeof actualShots === 'number' ? actualShots : 0
      );

      // 입고 이력 자동 생성
      await moldHistoryService.create({
        moldId: selectedMold.id || '',
        type: 'inbound',
        title: '입고',
        description: description || `생산 완료 후 입고 (쇼트: ${actualShots || 0})`,
        date: new Date(),
        shots: typeof actualShots === 'number' ? actualShots : 0,
      });
      
      // 입고 후 업데이트된 금형 정보 가져오기
      const updatedMold = await moldService.getMoldById(selectedMold.id || '');
      
      // 입고 알림 발송 (백그라운드)
      if (updatedMold) {
        notificationService.sendCheckInAlert(user.uid, updatedMold, transaction).catch(err => {
          console.error('입고 알림 발송 실패:', err);
        });
      }
      
      setMessage({type: 'success', text: '입고 처리가 완료되었습니다.'});
      setSelectedMold(null);
      setActualShots('');
      setDescription('');
      loadInUseMolds(); // 목록 새로고침
    } catch (error) {
      setMessage({type: 'error', text: '입고 처리에 실패했습니다.'});
    }
    setLoading(false);
  };

  return (
    <Card sx={{ height: 'fit-content' }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          금형 입고
        </Typography>
        
        {message && (
          <Alert severity={message.type} sx={{ mb: { xs: 2, md: 2 } }}>
            {message.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, md: 3 } }}>
          <Box>
            <Autocomplete
              options={inUseMolds}
              getOptionLabel={(option) => `${option.moldNumber} - ${option.moldName}`}
              value={selectedMold}
              onChange={(_, newValue) => setSelectedMold(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="금형 선택"
                  required
                  fullWidth
                  size="medium"
                />
              )}
            />
          </Box>

          <Box>
            <TextField
              label="실제 쇼트 수"
              type="number"
              value={actualShots}
              onChange={(e) => {
                const val = e.target.value;
                setActualShots(val === '' ? '' : Number(val));
              }}
              fullWidth
              size="medium"
              helperText="사용된 총 쇼트 수를 입력해주세요"
            />
          </Box>

          <Box>
            <TextField
              label="상세 설명 (선택)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="medium"
              multiline
              rows={2}
              helperText="입고 시 특이사항이나 상세 내용을 입력해주세요"
            />
          </Box>

          {/* 생산이력 정보 표시 */}
          {selectedMold && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'primary.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.200',
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                생산이력 조회 결과
              </Typography>
              {loadingProduction ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">조회 중...</Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    입력된 생산기록: {productionLogCount}건
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    총 생산량: {productionTotal.toLocaleString()} 쇼트
                  </Typography>
                  {productionLogCount === 0 && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                      ⚠️ 입력된 생산기록이 없습니다. 실제 쇼트수를 직접 입력해주세요.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          <Box>
            <Button
              variant="contained"
              onClick={handleCheckIn}
              disabled={loading}
              fullWidth
              sx={{ 
                height: { xs: 48, md: 40 },
                fontSize: { xs: '1rem', md: '0.875rem' },
                fontWeight: 'bold'
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : '입고 처리'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// 메인 Transaction 컴포넌트
const Transaction: React.FC = () => {
  const location = useLocation();
  const locationState = location.state as { 
    moldId?: string; 
    moldNumber?: string; 
    status?: string 
  } | null;
  
  // 상태에 따라 초기 탭 설정: in-use → 1(입고), stored → 0(출고)
  const initialTab = locationState?.status === 'in-use' ? 1 : 0;
  const [tabValue, setTabValue] = useState(initialTab);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        입출고 관리
      </Typography>
      
      <Paper sx={{ mt: { xs: 2, md: 2 } }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: { xs: '0.9rem', md: '0.875rem' },
              minHeight: { xs: 48, md: 48 },
              fontWeight: 'bold'
            }
          }}
          variant="fullWidth"
        >
          <Tab label="출고" />
          <Tab label="입고" />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          <CheckOutForm initialMoldId={locationState?.moldId} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <CheckInForm initialMoldId={locationState?.moldId} />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Transaction;
